// Prepared demo song data. In Build 1 this static data replaces a real
// SongAnalyzer. All three tracks share the included backing audio
// (/audio/adaptive-jam-demo.mp3) which is easily replaceable — see README
// note in this file's header comment and /public/audio.

import { Song, Zone, TimelineEvent, FretNote } from "@/models/types";

const AUDIO = "/audio/adaptive-jam-demo.mp3";

const n = (
  string: number,
  fret: number,
  note: string,
  role: FretNote["role"]
): FretNote => ({ string, fret, note, role });

// A minor pentatonic — Box 1 (position 5)
const ZONE_A: Zone = {
  id: "A",
  label: "Am · Position 1",
  position: 5,
  notes: [
    n(1, 5, "A", "root"), n(1, 8, "C", "safe"),
    n(2, 5, "E", "safe"), n(2, 8, "G", "passing"),
    n(3, 5, "C", "safe"), n(3, 7, "D", "safe"),
    n(4, 5, "G", "passing"), n(4, 7, "A", "root"),
    n(5, 5, "D", "safe"), n(5, 7, "E", "safe"),
    n(6, 5, "A", "root"), n(6, 8, "C", "safe"),
  ],
  recoveryNotes: [
    n(6, 5, "A", "recovery"),
    n(6, 8, "C", "recovery"),
    n(5, 7, "E", "recovery"),
  ],
};

// A minor pentatonic — Box 2 (position 7) — the position shift
const ZONE_B: Zone = {
  id: "B",
  label: "Am · Position 2",
  position: 7,
  notes: [
    n(1, 8, "C", "safe"), n(1, 10, "D", "safe"),
    n(2, 8, "G", "passing"), n(2, 10, "A", "root"),
    n(3, 7, "D", "safe"), n(3, 9, "E", "safe"),
    n(4, 7, "A", "root"), n(4, 10, "C", "safe"),
    n(5, 7, "E", "safe"), n(5, 10, "G", "passing"),
    n(6, 8, "C", "safe"), n(6, 10, "D", "safe"),
  ],
  recoveryNotes: [
    n(4, 7, "A", "recovery"),
    n(4, 10, "C", "recovery"),
    n(5, 7, "E", "recovery"),
  ],
};

const BPM = 90;
const BEAT = 60 / BPM;
const BAR = BEAT * 4;
const bar = (i: number) => +(i * BAR).toFixed(3);

// Per-bar chord map matching the generated backing track.
const CHORDS = [
  "Am", "Am", "Am", "Am",
  "Am", "G", "F", "Am", "Am", "G", "F", "Am",
  "F", "G", "F", "G",
  "C", "G", "Am", "F", "C", "G", "Am", "F",
  "Am", "G", "F", "Am", "Am", "G", "F", "Am",
  "Am", "Am", "E7", "Am",
];

/** Build the fully timed timeline for the 96s demo track. */
function buildTimeline(introMsg: string): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Section + zone anchors
  events.push({ time: bar(0), section: "Intro", chord: "Am", activeZone: "A", message: introMsg });
  events.push({ time: bar(4), section: "Verse", chord: "Am", message: "YOU'RE IN" });
  events.push({ time: bar(12), section: "Build", chord: "F" });

  // First position shift into the Chorus (Zone A -> Zone B)
  events.push({ time: bar(14), prepareNextZone: "B" });
  events.push({ time: bar(16), transition: true, toZone: "B", section: "Chorus", chord: "C", activeZone: "B", message: "OPEN" });

  // Return shift back to Zone A for Verse 2
  events.push({ time: bar(22), prepareNextZone: "A" });
  events.push({ time: bar(24), transition: true, toZone: "A", section: "Verse 2", chord: "Am", activeZone: "A", message: "STAY" });

  events.push({ time: bar(32), section: "Outro", chord: "Am", message: "LISTEN" });

  // Fill in every bar's chord so currentChord always tracks the track.
  for (let i = 0; i < CHORDS.length; i++) {
    events.push({ time: bar(i) + 0.001, chord: CHORDS[i] });
  }

  return events.sort((a, b) => a.time - b.time);
}

export const DEMO_SONGS: Song[] = [
  {
    id: "amber-drift",
    title: "Amber Drift",
    style: "Neo-Soul",
    bpm: BPM,
    timeSignature: [4, 4],
    key: "A minor",
    difficulty: "Gentle",
    duration: 96,
    audioUrl: AUDIO,
    progression: ["Am", "G", "F", "Am", "C", "G", "Am", "F"],
    needs: [
      "A minor pentatonic — Position 1",
      "One calm position shift to Position 2",
      "A relaxed 2-note groove",
      "Steady quarter-note pulse",
    ],
    zones: { A: ZONE_A, B: ZONE_B },
    timeline: buildTimeline("LISTEN"),
    accent: "neo-soul",
  },
  {
    id: "midnight-groove",
    title: "Midnight Groove",
    style: "Slow Blues",
    bpm: BPM,
    timeSignature: [4, 4],
    key: "A minor",
    difficulty: "Fluid",
    duration: 96,
    audioUrl: AUDIO,
    progression: ["Am", "Am", "F", "G", "Am", "C", "G", "Am"],
    needs: [
      "A minor pentatonic — Position 1",
      "Confident bends around the root",
      "One position move for the turnaround",
      "Feel the back-beat",
    ],
    zones: { A: ZONE_A, B: ZONE_B },
    timeline: buildTimeline("HOLD"),
    accent: "blues",
  },
  {
    id: "velvet-minor",
    title: "Velvet Minor",
    style: "Rock Ballad",
    bpm: BPM,
    timeSignature: [4, 4],
    key: "A minor",
    difficulty: "Energetic",
    duration: 96,
    audioUrl: AUDIO,
    progression: ["Am", "F", "C", "G", "Am", "F", "G", "Am"],
    needs: [
      "A minor pentatonic — Position 1 & 2",
      "Bigger sustained root notes",
      "Lift into Position 2 for the chorus",
      "Let the chords breathe",
    ],
    zones: { A: ZONE_A, B: ZONE_B },
    timeline: buildTimeline("READY"),
    accent: "ballad",
  },
];

export const getSongById = (id: string): Song | undefined =>
  DEMO_SONGS.find((s) => s.id === id);

export const FRET_MIN = 3;
export const FRET_MAX = 12;
export const INLAY_FRETS = [3, 5, 7, 9];
export const DOUBLE_INLAY_FRETS = [12];
export const STRINGS = [1, 2, 3, 4, 5, 6]; // 1 = high E (top), 6 = low E
